import React from 'react';
import './my-merits.scss';
import MeritApi from '../../../apis/merit-api';
import CommonApi from '../../../apis/common';
import CommonUtils from '../../../utility/common-utilities';
import { ImageConstants } from '../../../constants/constants';
import { getImageSource } from '../../../utility/image-source';
import routerHistory from '../../routes/history';
import Path from '../../routes/routes-path';
import SharedProfile from '../../../../shared-storage/profile';



class MyMerits extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            meritData: { selfLearningMerits: 0, totalActivityMerits: 0 },
            history: [],
            isLoading: true,
            filterType: 'ALL',
            selectedMonths: [],
            showHowMeritsModal: false,
            expandedMultBand: null,
            meritConfig: null,
            expandedRows: {}
        };
    }

    toggleRow = (index) => {
        this.setState(prevState => {
            const expandedRows = { ...prevState.expandedRows };
            if (expandedRows[index]) {
                delete expandedRows[index];
            } else {
                expandedRows[index] = true;
            }
            return { expandedRows };
        });
    };

    componentDidMount() {
        this.fetchMeritData();
    }

    fetchMeritData() {
        const userInfo = SharedProfile.getProfile();
        const subjectId = userInfo ? userInfo.subjectId : null;
        Promise.all([
            MeritApi.getStudentMerits({ subjectId }),
            MeritApi.getStudentMeritHistory({ limit: 50, subjectId }),
            CommonApi.getConfigurationsList({ type: 'MERIT_SYSTEM_CONFIG' }).catch(() => null)
        ]).then(([meritsResponse, historyResponse, configResponse]) => {
            let meritConfig = null;
            if (configResponse && configResponse.response && configResponse.response.length) {
                const configItem = configResponse.response.find(c => c.type === 'MERIT_SYSTEM_CONFIG' || c.configurationType === 'MERIT_SYSTEM_CONFIG') || configResponse.response[0];
                if (configItem) {
                    meritConfig = configItem.config || configItem;
                }
            }

            this.setState({
                meritData: meritsResponse.data || this.state.meritData,
                history: historyResponse.data || [],
                meritConfig: meritConfig,
                isLoading: false
            });
        }).catch(err => {
            console.error("Error fetching merit data", err);
            this.setState({ isLoading: false });
        });
    }

    render() {
        const { meritData, history, isLoading, filterType, selectedMonths = [], showHowMeritsModal, expandedMultBand, meritConfig, expandedRows = {} } = this.state;

        if (isLoading) {
            return <div className="text-center mt-5">Loading...</div>;
        }

        const uniqueMonths = [];
        const monthKeys = new Set();
        history.forEach(entry => {
            if (entry.dayStart) {
                const date = new Date(entry.dayStart);
                const year = date.getFullYear();
                const month = date.getMonth();
                const key = `${year}-${String(month + 1).padStart(2, '0')}`;
                if (!monthKeys.has(key)) {
                    monthKeys.add(key);
                    const label = date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                    const shortLabel = date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
                    uniqueMonths.push({ key, label, shortLabel, sortKey: year * 12 + month });
                }
            }
        });
        uniqueMonths.sort((a, b) => b.sortKey - a.sortKey);

        const rows = [];
        history.forEach((entry) => {
            const dateObj = new Date(entry.dayStart);
            const formattedDate = dateObj.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
            const monthVal = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;

            Object.entries(entry.activityLevel || {}).forEach(([actType, item]) => {
                const processAct = (actData) => {
                    const baseMerits = actData.baseMerits !== undefined ? actData.baseMerits : (actData.activityMerits || 0);
                    const totalMerits = actData.activityMerits !== undefined ? Number(actData.activityMerits.toFixed(2)) : 0;
                    const bonusMerits = Number(Math.max(0, totalMerits - baseMerits).toFixed(2));
                    rows.push({
                        date: formattedDate,
                        shortDate: dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
                        monthVal: monthVal,
                        actType: actType.replace(/_/g, ' ').toUpperCase(),
                        rawActType: actType,
                        chapterName: actData.chapterName || '-',
                        accuracy: actData.accuracy !== undefined && actData.accuracy !== null ? `${actData.accuracy}%` : '-',
                        accuracyVal: actData.accuracy,
                        merits: totalMerits,
                        baseMerits: baseMerits,
                        bonusMerits: bonusMerits
                    });
                };
                if (Array.isArray(item)) {
                    item.forEach(processAct);
                } else {
                    processAct(item);
                }
            });
        });

        const filteredRows = rows.filter(row => {
            let matchesType = true;
            if (filterType === 'SELF') {
                // Self activities: self-learning, challenge, practice
                matchesType = (
                    row.rawActType.startsWith('self_') ||
                    row.rawActType === 'challenge_questions' ||
                    row.rawActType === 'practice'
                );
            } else if (filterType === 'SCHOOL') {
                // School activities: school assignments, classwork, homework, assessments
                matchesType = (
                    row.rawActType.startsWith('school_') ||
                    row.rawActType === 'school_assessment' ||
                    row.rawActType === 'classwork' ||
                    row.rawActType === 'homework'
                );
            }

            let matchesMonth = true;
            if (selectedMonths.length > 0) {
                matchesMonth = selectedMonths.includes(row.monthVal);
            }

            return matchesType && matchesMonth;
        });

        // Compute summary merits based on selectedMonth
        let displayTotalMerits = 0;
        let displaySelfLearningMerits = 0;

        rows.forEach(row => {
            const matchesMonth = selectedMonths.length === 0 || selectedMonths.includes(row.monthVal);
            if (matchesMonth) {
                displayTotalMerits += row.merits;

                const isSelf = (
                    row.rawActType.startsWith('self_') ||
                    row.rawActType === 'challenge_questions' ||
                    row.rawActType === 'practice'
                );
                if (isSelf) {
                    displaySelfLearningMerits += row.merits;
                }
            }
        });

        return (
            <div className="my-merits-page-bg">
                <div className="my-merits-container container mt-4">
                    <div className="row mb-4">
                        <div className="col-12">
                            <div className="merits-header-row mb-3">
                                <h2 className="merit-title mb-0">My Merits</h2>
                                <div className="merit-buttons">
                                    <button className="btn how-merits-work-btn mr-2" onClick={() => this.setState({ showHowMeritsModal: true })}>
                                        How Merits Work
                                    </button>
                                    <button className="btn view-leaderboard-btn" onClick={() => (this.props.history || routerHistory).push(Path.LEADERBOARD)}>
                                        View Leaderboard
                                    </button>
                                </div>
                            </div>
                            <div className="merit-dashboard-card p-4 d-flex justify-content-around align-items-center">
                                <div className="text-center">
                                    {/* <div className="merit-stat-icon">⭐</div> */}
                                    <div className="merit-value">{CommonUtils.convertToFixed(displayTotalMerits, 2)}</div>
                                    <div className="merit-label">Total Merits</div>
                                </div>
                                <div className="merit-stat-divider"></div>
                                <div className="text-center">
                                    {/* <div className="merit-stat-icon">📚</div> */}
                                    <div className="merit-value">{CommonUtils.convertToFixed(displaySelfLearningMerits, 2)}</div>
                                    <div className="merit-label">Self Learning</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="d-flex justify-content-between align-items-center flex-wrap merit-history-header-row mb-3">
                                <h4 className="history-title mb-0">Activity History</h4>
                                <div className="merit-filters-group">
                                    <div className="merit-filter-container">
                                        <label className="merit-filter-label">FILTER:</label>
                                        <select
                                            className="merit-filter-select"
                                            value={this.state.filterType}
                                            onChange={(e) => this.setState({ filterType: e.target.value })}
                                        >
                                            <option value="ALL">All Activities</option>
                                            <option value="SELF">Self Activities</option>
                                            <option value="SCHOOL">School Activities</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                            {history.length > 0 ? (
                                <>
                                    <div className="merit-month-filter-row">
                                        <div className="merit-month-buttons-container">
                                            <button
                                                className={`merit-month-btn ${selectedMonths.length === 0 ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    if (e && e.currentTarget && e.currentTarget.scrollIntoView) {
                                                        e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                                                    }
                                                    this.setState({ selectedMonths: [] });
                                                }}
                                            >
                                                <span className="month-label-full">All Months</span>
                                                <span className="month-label-short">All Months</span>
                                            </button>
                                            {uniqueMonths.map(month => {
                                                const isSelected = selectedMonths.includes(month.key);
                                                return (
                                                    <button
                                                        key={month.key}
                                                        className={`merit-month-btn ${isSelected ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            if (e && e.currentTarget && e.currentTarget.scrollIntoView) {
                                                                e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                                                            }
                                                            this.setState(prevState => {
                                                                const nextSelected = prevState.selectedMonths.includes(month.key)
                                                                    ? prevState.selectedMonths.filter(m => m !== month.key)
                                                                    : [...prevState.selectedMonths, month.key];
                                                                return { selectedMonths: nextSelected };
                                                            });
                                                        }}
                                                    >
                                                        <span className="month-label-full">{month.label}</span>
                                                        <span className="month-label-short">{month.shortLabel}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {(selectedMonths.length > 0 || filterType !== 'ALL') && (
                                            <div className="merit-month-clear-wrapper">
                                                <button
                                                    className="merit-month-clear-btn"
                                                    onClick={() => this.setState({ selectedMonths: [], filterType: 'ALL' })}
                                                    title="Clear filters"
                                                    aria-label="Clear filters"
                                                >
                                                    <span className="clear-btn-full">Clear Filters</span>
                                                    <span className="clear-btn-short" aria-hidden="true">✕</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="table-responsive merit-history-table-wrapper">
                                        <table className="table merit-history-table">
                                            <thead>
                                                <tr>
                                                    <th>DATE</th>
                                                    <th>ACTIVITY NAME</th>
                                                    <th>CHAPTER</th>
                                                    <th>BASE MERIT</th>
                                                    <th>ACCURACY %</th>
                                                    <th>BONUS MERIT</th>
                                                    <th>TOTAL MERIT</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRows.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center text-muted p-4">
                                                            No activities found matching this filter.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    filteredRows.map((row, index) => {
                                                        const isExpanded = !!expandedRows[index];
                                                        return (
                                                            <tr
                                                                key={index}
                                                                className={`merit-history-row ${isExpanded ? 'is-expanded' : 'is-collapsed'}`}
                                                                onClick={() => this.toggleRow(index)}
                                                            >
                                                                {/* Mobile 2-line summary cell */}
                                                                <td className="col-mobile-summary" colSpan="7">
                                                                    <div className="mobile-card-line1">
                                                                        <span className="card-chapter-name">{row.chapterName}</span>
                                                                        <span className="card-dot">•</span>
                                                                        <span className="badge merit-badge">{row.actType}</span>
                                                                    </div>
                                                                    <div className="mobile-card-line2">
                                                                        <div className="mobile-card-line2-left">
                                                                            <span className="mobile-expand-hint">
                                                                                {isExpanded ? '▲ Hide details' : '▼ Details'}
                                                                            </span>
                                                                            <span className="card-dot">•</span>
                                                                            <span className="card-date-short">{row.shortDate}</span>
                                                                        </div>
                                                                        <div className="mobile-total-merit">
                                                                            <span className="total-label">Total Merit:</span>
                                                                            <span className="total-merit-val">{Number(row.merits).toFixed(2)}</span>
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* Desktop Table Columns */}
                                                                <td className="col-date" data-label="Date">{row.date}</td>
                                                                <td className="col-activity-name" data-label="Activity">
                                                                    <span className="badge merit-badge">
                                                                        {row.actType}
                                                                    </span>
                                                                </td>
                                                                <td className="col-chapter" data-label="Chapter">{row.chapterName}</td>
                                                                <td className="col-base-merit" data-label="Base Merit">{Number(row.baseMerits).toFixed(2)}</td>
                                                                <td className="col-accuracy" data-label="Accuracy">
                                                                    {row.accuracy !== '-' ? (
                                                                        <span className={row.accuracyVal >= 70 ? "text-success font-weight-bold" : "text-warning font-weight-bold"}>
                                                                            {row.accuracy}
                                                                        </span>
                                                                    ) : '-'}
                                                                </td>
                                                                <td className="col-bonus-merit" data-label="Bonus Merit">
                                                                    {row.bonusMerits > 0 ? <span className="text-success font-weight-bold">+{Number(row.bonusMerits).toFixed(2)}</span> : <span className="text-muted">0.00</span>}
                                                                </td>
                                                                <td className="col-total-merit" data-label="Total Merit">
                                                                    <span className="total-merit-val">{Number(row.merits).toFixed(2)}</span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            ) : (
                                <div className="text-center text-muted p-4">
                                    No recent activity found. Start completing assignments to earn merits!
                                </div>
                            )}
                        </div>
                    </div>
                    {showHowMeritsModal && (
                        <div className="merits-modal-overlay" onClick={() => this.setState({ showHowMeritsModal: false })}>
                            <div className="merits-modal-content animate-slide-up" onClick={(e) => e.stopPropagation()}>
                                <button className="close-modal-btn" onClick={() => this.setState({ showHowMeritsModal: false })}>
                                    &times;
                                </button>

                                <div className="guide-hero">
                                    <div className="hero-dot dot-1"></div>
                                    <div className="hero-dot dot-2"></div>
                                    <div className="hero-dot dot-3"></div>
                                    <span className="hero-badge">⭐ Countingwell Merit System</span>
                                    <h1>How Merits Work</h1>
                                    <p>Every time you learn, you earn. Here's everything you need to know about Merits, multipliers, streaks, and the awards you can win!</p>
                                </div>

                                <div className="guide-container">
                                    <div className="guide-section">
                                        <div className="section-label">The Basics</div>
                                        <h3 className="section-title">Three ways to earn more</h3>
                                        <p className="section-sub">Merits are the points you earn for learning. The more you do, the more you earn — and there are three ways to boost your total.</p>

                                        <div className="pillars">
                                            <div className="pillar">
                                                <div className="pillar-icon pillar-green"><span>✅</span></div>
                                                <h4>Complete Activities</h4>
                                                <p>Finish any practice, assessment, or lesson to earn base Merits instantly.</p>
                                            </div>
                                            <div className="pillar">
                                                <div className="pillar-icon pillar-purple"><span>🎯</span></div>
                                                <h4>Score Well</h4>
                                                <p>Higher accuracy means a bigger multiplier — your Merits grow even more.</p>
                                            </div>
                                            <div className="pillar">
                                                <div className="pillar-icon pillar-orange"><span>🔥</span></div>
                                                <h4>Stay Consistent</h4>
                                                <p>Show up regularly each week to earn an extra streak bonus.</p>
                                            </div>
                                        </div>

                                        <div className="formula-box">
                                            <div className="formula-text">Final Merits = <span>Base Merits</span> x <span>Accuracy Multiplier</span></div>
                                        </div>

                                    </div>
                                    <div className="guide-section">
                                        <div className="section-label">The Big Wins</div>
                                        <h3 className="section-title">Four ways to be recognised</h3>
                                        <p className="section-sub">Every month, the top students in each category get awarded — and you don't have to be a topper to win!</p>

                                        <div className="awards-grid">
                                            <div className="award-card">
                                                <div className="award-img-wrap"><span className="award-emoji">🥇</span></div>
                                                <div className="award-body">
                                                    <span className="award-freq freq-monthly">MONTHLY</span>
                                                    <div className="award-name">Top Performer</div>
                                                    <div className="award-desc">Highest total Merits in the month</div>
                                                </div>
                                            </div>

                                            <div className="award-card">
                                                <div className="award-img-wrap"><span className="award-emoji">🔥</span></div>
                                                <div className="award-body">
                                                    <span className="award-freq freq-quarterly">QUARTERLY</span>
                                                    <div className="award-name">Consistency Champion</div>
                                                    <div className="award-desc">Most active weeks across the quarter</div>
                                                </div>
                                            </div>

                                            <div className="award-card">
                                                <div className="award-img-wrap"><span className="award-emoji">🚀</span></div>
                                                <div className="award-body">
                                                    <span className="award-freq freq-monthly">MONTHLY</span>
                                                    <div className="award-name">Rising Star</div>
                                                    <div className="award-desc">Biggest improvement from last month</div>
                                                </div>
                                            </div>

                                            <div className="award-card">
                                                <div className="award-img-wrap"><span className="award-emoji">📚</span></div>
                                                <div className="award-body">
                                                    <span className="award-freq freq-monthly">MONTHLY</span>
                                                    <div className="award-name">Self Learning Leader</div>
                                                    <div className="award-desc">Most Merits from self-driven learning</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="guide-section">
                                        <div className="section-label">Earning Base Merits & Qualifications</div>
                                        <h3 className="section-title">What you do, what you earn, and eligible awards</h3>
                                        <p className="section-sub">Every activity you complete earns you base Merits. See how they count towards monthly awards.</p>

                                        <div className="table-responsive award-qualification-table-wrapper">
                                            <table className="table award-qualification-table">
                                                <thead>
                                                    <tr>
                                                        <th>Activity Type</th>
                                                        <th className="text-center">Base Merits</th>
                                                        <th className="text-center">Top Performer</th>
                                                        <th className="text-center">Self Learning Leader</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td>
                                                            <div className="act-name">School Practice</div>
                                                            <div className="act-desc">Practice set by your teacher</div>
                                                        </td>
                                                        <td className="text-center font-weight-bold">+40</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                        <td className="text-center text-muted">❌</td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <div className="act-name">School Assessment</div>
                                                            <div className="act-desc">Test or assessment set by your teacher</div>
                                                        </td>
                                                        <td className="text-center font-weight-bold">+50</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                        <td className="text-center text-muted">❌</td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <div className="act-name">Self Practice</div>
                                                            <div className="act-desc">Practice you start on your own</div>
                                                        </td>
                                                        <td className="text-center font-weight-bold">+15</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                    </tr>
                                                    <tr>
                                                        <td>
                                                            <div className="act-name">Self Assessment</div>
                                                            <div className="act-desc">A mock test you take on your own</div>
                                                        </td>
                                                        <td className="text-center font-weight-bold">+50</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                    </tr>
                                                    <tr className="highlight">
                                                        <td>
                                                            <div className="act-name font-weight-bold">⚡ Challenge Questions</div>
                                                            <div className="act-desc text-muted">Special tricky questions from Countingwell</div>
                                                        </td>
                                                        <td className="text-center font-weight-bold">+100</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                        <td className="text-center text-success font-weight-bold">✅</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="guide-section">
                                        <div className="section-label">Score Boost</div>
                                        <h3 className="section-title">The accuracy multiplier</h3>
                                        <p className="section-sub">Your accuracy in an activity decides how much your Merits get multiplied. The better you score, the bigger the boost!</p>

                                        <div className="mult-list">
                                            <div className="mult-header-row">
                                                <span className="mult-header-label">Accuracy %</span>
                                                <span className="mult-header-val">Multiplier</span>
                                            </div>
                                            {/* 100% - fixed */}
                                            <div className="mult-row">
                                                <div className="mult-label">100%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '100%' }}></div></div>
                                                <div className="mult-val">1.5x</div>
                                                <div className="mult-arrow"></div>
                                            </div>

                                            {/* 90% - 99% expandable band */}
                                            <div
                                                className={`mult-row mult-row-expandable${expandedMultBand === '90' ? ' mult-row-open' : ''}`}
                                                onClick={() => this.setState({ expandedMultBand: expandedMultBand === '90' ? null : '90' })}
                                            >
                                                <div className="mult-label">90% - 99%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '90%' }}></div></div>
                                                <div className="mult-val">1.4x - 1.49x</div>
                                                <div className="mult-arrow">{expandedMultBand === '90' ? '▴' : '▾'}</div>
                                            </div>
                                            {expandedMultBand === '90' && (
                                                <div className="mult-sub-list">
                                                    {[99, 98, 97, 96, 95, 94, 93, 92, 91, 90].map(pct => {
                                                        const m = (1.4 + (pct - 90) * 0.01).toFixed(2);
                                                        const barW = `${pct}%`;
                                                        return (
                                                            <div className="mult-sub-row" key={pct}>
                                                                <div className="mult-sub-label">{pct}%</div>
                                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: barW }}></div></div>
                                                                <div className="mult-sub-val">{m}x</div>
                                                                <div className="mult-arrow"></div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* 80% - 89% expandable band */}
                                            <div
                                                className={`mult-row mult-row-expandable${expandedMultBand === '80' ? ' mult-row-open' : ''}`}
                                                onClick={() => this.setState({ expandedMultBand: expandedMultBand === '80' ? null : '80' })}
                                            >
                                                <div className="mult-label">80% - 89%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '80%' }}></div></div>
                                                <div className="mult-val">1.3x - 1.39x</div>
                                                <div className="mult-arrow">{expandedMultBand === '80' ? '▴' : '▾'}</div>
                                            </div>
                                            {expandedMultBand === '80' && (
                                                <div className="mult-sub-list">
                                                    {[89, 88, 87, 86, 85, 84, 83, 82, 81, 80].map(pct => {
                                                        const m = (1.3 + (pct - 80) * 0.01).toFixed(2);
                                                        const barW = `${pct}%`;
                                                        return (
                                                            <div className="mult-sub-row" key={pct}>
                                                                <div className="mult-sub-label">{pct}%</div>
                                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: barW }}></div></div>
                                                                <div className="mult-sub-val">{m}x</div>
                                                                <div className="mult-arrow"></div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Static lower bands */}
                                            <div className="mult-row">
                                                <div className="mult-label">75% - 79%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '75%' }}></div></div>
                                                <div className="mult-val">1.25x</div>
                                                <div className="mult-arrow"></div>
                                            </div>
                                            <div className="mult-row">
                                                <div className="mult-label">70% - 74%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '70%' }}></div></div>
                                                <div className="mult-val">1.2x</div>
                                                <div className="mult-arrow"></div>
                                            </div>
                                            <div className="mult-row">
                                                <div className="mult-label">60% - 69%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '60%' }}></div></div>
                                                <div className="mult-val">1.1x</div>
                                                <div className="mult-arrow"></div>
                                            </div>
                                            <div className="mult-row">
                                                <div className="mult-label">50% - 59%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '50%' }}></div></div>
                                                <div className="mult-val">1.05x</div>
                                                <div className="mult-arrow"></div>
                                            </div>
                                            <div className="mult-row">
                                                <div className="mult-label">Below 50%</div>
                                                <div className="mult-bar-bg"><div className="mult-bar-fill" style={{ width: '20%' }}></div></div>
                                                <div className="mult-val">1.0x</div>
                                                <div className="mult-arrow"></div>
                                            </div>
                                        </div>
                                        <div className="mult-tip mt-3 text-center text-muted">
                                            💡 <em>Pro-tip: For scores between 80% and 99%, the multiplier increases by 0.01× for every 1% increase in accuracy!</em>
                                        </div>
                                    </div>


                                    <div className="guide-section">
                                        <div className="section-label">Consistency</div>
                                        <h3 className="section-title">Weekly streak bonus</h3>
                                        <p className="section-sub">Earn at least {meritConfig && meritConfig.ACTIVE_DAY_THRESHOLD ? meritConfig.ACTIVE_DAY_THRESHOLD : 40} Merits in a day to make it an "active day." String together active days each week for bonus Merits.</p>

                                        <div className="streak-row">
                                            {meritConfig && meritConfig.STREAK_BONUS_TIERS ? (
                                                [...meritConfig.STREAK_BONUS_TIERS]
                                                    .sort((a, b) => a.minDays - b.minDays)
                                                    .map(streak => {
                                                        if (streak.bonus === 0) return null;
                                                        return (
                                                            <div className="streak-chip" key={streak.minDays}>
                                                                <div className="streak-lbl">{streak.minDays}-Day Streak</div>
                                                                <div className="streak-num">+{streak.bonus}</div>
                                                            </div>
                                                        );
                                                    })
                                            ) : (
                                                <>
                                                    <div className="streak-chip">
                                                        <div className="streak-lbl">2-Day Streak</div>
                                                        <div className="streak-num">+20</div>
                                                    </div>
                                                    <div className="streak-chip">
                                                        <div className="streak-lbl">3-Day Streak</div>
                                                        <div className="streak-num">+30</div>
                                                    </div>
                                                    <div className="streak-chip">
                                                        <div className="streak-lbl">4-Day Streak</div>
                                                        <div className="streak-num">+40</div>
                                                    </div>
                                                    <div className="streak-chip">
                                                        <div className="streak-lbl">5-Day Streak</div>
                                                        <div className="streak-num">+50</div>
                                                    </div>
                                                    <div className="streak-chip">
                                                        <div className="streak-lbl">6-Day Streak</div>
                                                        <div className="streak-num">+60</div>
                                                    </div>
                                                    <div className="streak-chip">
                                                        <div className="streak-lbl">7-Day Streak</div>
                                                        <div className="streak-num">+75</div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="guide-section">
                                        <div className="section-label">What You Get</div>
                                        <h3 className="section-title">Recognition for top 3</h3>
                                        <p className="section-sub">If you finish in the top 3 of any category, here's what you take home:</p>

                                        <div className="recog-row">
                                            <div className="recog-card recog-1">
                                                <div className="recog-medal">🥇</div>
                                                <div className="recog-place">1st Place</div>
                                                <div className="recog-items-list">
                                                    <div className="recog-item">✓ Physical Badge</div>
                                                    <div className="recog-item">✓ Digital Badge</div>
                                                    <div className="recog-item">✓ Premium Sticker</div>
                                                </div>
                                            </div>
                                            <div className="recog-card recog-2">
                                                <div className="recog-medal">🥈</div>
                                                <div className="recog-place">2nd Place</div>
                                                <div className="recog-items-list">
                                                    <div className="recog-item">✓ Digital Badge</div>
                                                    <div className="recog-item">✓ Notebook Sticker</div>
                                                </div>
                                            </div>
                                            <div className="recog-card recog-3">
                                                <div className="recog-medal">🥉</div>
                                                <div className="recog-place">3rd Place</div>
                                                <div className="recog-items-list">
                                                    <div className="recog-item">✓ Digital Badge</div>
                                                    <div className="recog-item">✓ Notebook Sticker</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="tip-box">
                                            <div className="tip-icon">💡</div>
                                            <div className="tip-text">Tip: You don't need to be the topper in class to win! Rising Star rewards improvement, and Self Learning Leader rewards curiosity — anyone can win these.</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="guide-footer">Countingwell Merit System · Keep learning, keep earning! ⭐</div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default MyMerits;